import { RefObject, useLayoutEffect, useState } from 'react';

import { computeVisibleTabCount } from './tabOverflow';

interface Params {
  itemCount: number;
  /** The row whose width is the space the items may take. */
  rowRef: RefObject<HTMLElement | null>;
  /**
   * A hidden twin row that always renders every item at its natural width, followed by one
   * absolutely positioned element holding the overflow separator and menu trigger. Out of flow, the
   * menu twin is measured without widening the twin row, so the row's own width stays the items plus
   * the list's padding and gaps.
   */
  measureRef: RefObject<HTMLElement | null>;
}

/**
 * How many leading items of a selection-control row fit beside the overflow menu; null until the
 * row is laid out. Re-measures when either the space we have (viewport, sidebar) or the space we
 * need (labels, counts, fonts, the menu label) changes.
 */
export const useOverflowVisibleCount = ({ itemCount, rowRef, measureRef }: Params): number | null => {
  const [visibleCount, setVisibleCount] = useState<number | null>(null);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const measured = measureRef.current;
    if (!row || !measured) {
      setVisibleCount(null);
      return;
    }

    const update = () => {
      const widths = Array.from(measured.children).map((node) => node.getBoundingClientRect().width);
      const itemWidths = widths.slice(0, itemCount);
      const [menuWidth = 0] = widths.slice(itemCount);
      const totalItemWidth = itemWidths.reduce((sum, width) => sum + width, 0);

      setVisibleCount(
        computeVisibleTabCount({
          tabWidths: itemWidths,
          menuButtonWidth: menuWidth,
          availableWidth: row.getBoundingClientRect().width,
          // Derived rather than hardcoded so a padding change in the list variant cannot drift from this.
          listChromeWidth: measured.getBoundingClientRect().width - totalItemWidth
        })
      );
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(row);
    // Each twin on its own: the out-of-flow menu twin never resizes the twin row, and one item can
    // grow while another shrinks by the same amount.
    Array.from(measured.children).forEach((node) => observer.observe(node));

    return () => observer.disconnect();
  }, [itemCount, rowRef, measureRef]);

  return visibleCount;
};
